import { create } from 'zustand'
import { PublicMenu, PublicMenuCategory, PublicMenuItem } from '../models/menu'
import { publicMenuService } from '../services'
import toast from 'react-hot-toast'

interface MenuState {
  menu: PublicMenu | null
  categories: PublicMenuCategory[]
  selectedCategory: PublicMenuCategory | null
  searchTerm: string
  isLoading: boolean
  error: string | null

  // Actions
  fetchPublicMenu: (businessId: string) => Promise<void>
  setSelectedCategory: (category: PublicMenuCategory | null) => void
  setSearchTerm: (term: string) => void
  searchItems: (businessId: string, term: string) => Promise<PublicMenuItem[]>
  clearMenu: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
}

export const useMenuStore = create<MenuState>((set) => ({
  menu: null,
  categories: [],
  selectedCategory: null,
  searchTerm: '',
  isLoading: false,
  error: null,

  fetchPublicMenu: async (businessId: string) => {
    try {
      set({ isLoading: true, error: null })
      const menu = await publicMenuService.getPublicMenu(businessId)
      set({ 
        menu,
        categories: menu.categories,
        selectedCategory: menu.categories[0] || null
      })
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to fetch menu'
      set({ error: errorMessage })
      toast.error(errorMessage)
    } finally {
      set({ isLoading: false })
    }
  },

  setSelectedCategory: (category: PublicMenuCategory | null) => {
    set({ selectedCategory: category })
  },

  setSearchTerm: (term: string) => {
    set({ searchTerm: term })
  },

  searchItems: async (businessId: string, term: string) => {
    try {
      if (!term.trim()) {
        return []
      }

      const items = await publicMenuService.searchItems(businessId, term)
      return items
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to search items'
      toast.error(errorMessage)
      return []
    }
  },

  clearMenu: () => {
    set({
      menu: null,
      categories: [],
      selectedCategory: null,
      searchTerm: '',
      error: null
    })
  },

  setLoading: (loading: boolean) => set({ isLoading: loading }),
  setError: (error: string | null) => set({ error })
}))